import { useState } from 'react';
import { MailX } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { useRevokeInvitation } from '@/pages/team/hooks/useTeamQueries';
import type { Invitation } from '@/pages/team/types';

interface RevokeInviteDialogProps {
  invitation: Invitation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Подтверждение отзыва приглашения — ссылка станет недействительной. */
export function RevokeInviteDialog({
  invitation,
  open,
  onOpenChange,
}: RevokeInviteDialogProps) {
  const revoke = useRevokeInvitation();
  const [error, setError] = useState<string | undefined>();

  if (!invitation) return null;

  const submit = async () => {
    setError(undefined);
    try {
      await revoke.mutateAsync(invitation.id);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Не удалось отозвать приглашение');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <MailX className="h-5 w-5" />
          </span>
          <DialogTitle>Отозвать приглашение?</DialogTitle>
          <DialogDescription>
            Ссылка для <span className="font-medium text-foreground">{invitation.email}</span>{' '}
            перестанет работать. Пригласить снова можно в любой момент.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={revoke.isPending}>
            Отмена
          </Button>
          <Button variant="destructive" onClick={submit} disabled={revoke.isPending}>
            {revoke.isPending ? 'Отзываем…' : 'Отозвать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
